import React, { useEffect } from 'react';

import { CardContent, Card, Theme, Avatar } from '@material-ui/core';
import { withStyles, WithStyles, createStyles, makeStyles, useTheme } from '@material-ui/core/styles';

import { LiveChatNotificationResponseNext } from '@alme/types/response/LiveChatNotificationResponse';
import { AlmeMarkdownResponseNext } from '../../../AlmeResponse/AlmeResponse';

import liveChatOperatorResponseStyles from './AlmeLiveChatOperatorResponse.styles';
import { FocusComponentProps } from '../../../../models/ComponentProps';

import { getAgentResponse, getAgentResponseText, onTriggerAutoNavUrl, paintNavUrl } from '../../../AlmeResponse/AlmeResponseUtils';
import { AlmeLiveChatOperatorResponseConfig } from './AlmeLiveChatOperatorResponseConfig';
import { convertToHex } from '../../../../utils/ColorUtils';
import { DisplayLink } from '@alme/types/response/DisplayLink';
import { NavUrl } from '@alme/types/response/NavUrl';
import { EntryExtensionData } from '@alme/types/response/EntryExtensionData';

export function isValidLiveChatOperatorResponse(response: LiveChatNotificationResponseNext): boolean {
    const responseText = getAgentResponseText(response);
    const hasIvaResponse = !!(responseText || response.navUrl || response.displayLinkCollection?.Sections?.length);

    return hasIvaResponse && response?.extensionData?.MessageOrigin === 'Operator';
}

const useStyles = makeStyles(({ spacing }: Theme) =>
    createStyles({
        navUrl: {
            marginTop: spacing(2),
        },
    }),
);

export type AlmeLiveChatOperatorResponseClasses = Record<"navUrl", string>;

interface AlmeLiveChatOperatorResponseProps extends WithStyles<typeof liveChatOperatorResponseStyles> {
    response: LiveChatNotificationResponseNext;
    isLastResponse: boolean;
    almeConfig: AlmeLiveChatOperatorResponseConfig;
    focusProps?: FocusComponentProps;
    responseId?: string | number;
    onDisplayLinkClick: (link: DisplayLink | NavUrl) => void;
}

const AlmeLiveChatOperatorResponse: React.FC<AlmeLiveChatOperatorResponseProps> = (props: AlmeLiveChatOperatorResponseProps) => {
    const classes: AlmeLiveChatOperatorResponseClasses = useStyles();
    const theme = useTheme();

    const response = {
        ...props.response,
        // TODO: add support for limited markdown in live chat (i.e. URL links).
        html: '',
    } as AlmeMarkdownResponseNext;

    useEffect(() => {
        if (response.navUrl && props.almeConfig.liveChatOperatorResponse.shouldAutoNav) {
            onTriggerAutoNavUrl(response.navUrl);
        }
        // Empty dependency-list so autoNav only happens once:
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!isValidLiveChatOperatorResponse(props.response)) {
        return null;
    }

    const responseRevision = (props.responseId || response.responseRevision).toString();
    const entryId = response.id || (response.extensionData && (response.extensionData as EntryExtensionData)?.entryId) || responseRevision;

    let avatar = props.almeConfig.liveChatOperatorResponse.chatIcon;

    const operatorName = props.response.extensionData.OperatorData?.Name;
    if (props.almeConfig.liveChatOperatorResponse.useUniqueChatIcons && operatorName) {
        const iconColor = theme.palette.augmentColor({ main: convertToHex(operatorName) });
        avatar = (<Avatar style={{ backgroundColor: iconColor.main, color: iconColor.contrastText }}>{operatorName.charAt(0)}</Avatar>);
    }

    if (avatar && operatorName) {
        avatar = React.cloneElement(avatar, { alt: operatorName, title: operatorName });
    }

    const responseText = getAgentResponse(response, `${props.classes.ivaCardBodyText}`, `${props.classes.inlineButton}`, props.onDisplayLinkClick);

    return (
        <div
            className={props.classes.ivaCardContainer}
            id={`alme-iva-live-chat-card-${responseRevision}`}>
            {avatar}
            <Card
                className={props.classes.ivaCard}
                key={`${entryId}-alme`}
                raised={props.isLastResponse}
            >
                <CardContent className={`${props.classes.ivaCardContent}`}>
                    {responseText}
                    {paintNavUrl(entryId, response.navUrl, responseRevision, classes, props.almeConfig, props.onDisplayLinkClick, props.focusProps, true)}
                </CardContent>
            </Card>
        </div>
    );
};

export default withStyles(liveChatOperatorResponseStyles)(AlmeLiveChatOperatorResponse);
